// @ts-check

// Vendor imports
const _ = require('lodash')
// Client V2 common imports
const useClientEffect = require('client-v2/common/hooks/client-effect')

// Default option values
const DEFAULT_SCROLL_THROTTLE_FREQUENCY = 100 // ms
const DEFAULT_LOAD_MORE_THRESHOLD = 40 // px

/**
 * @summary
 * React Hook that monitors the scroll state of an element
 * and fires a callback when scrolled near the bottom.
 * @param {Object} options
 * @param {boolean} options.disabled
 * @param {function} options.onLoadMore
 * Required callback that is called when scroll is within
 * the `options.loadMoreThreshold` from the bottom.
 * @param {number} [options.loadMoreThreshold] (in pixels)
 * @param {number} [options.scrollThrottleFrequency] (in milliseconds)
 * @param {React.RefObject<HTMLElement>} options.scrollableRef
 * @returns {void}
 */
function useInfiniteScroll({
  disabled,
  loadMoreThreshold = DEFAULT_LOAD_MORE_THRESHOLD,
  scrollThrottleFrequency = DEFAULT_SCROLL_THROTTLE_FREQUENCY,
  scrollableRef,
  onLoadMore
}) {
  useClientEffect(
    () => {
      const scrollableEl = scrollableRef.current
      if (disabled || !scrollableEl) {
        return
      }

      const onScroll = _.throttle(() => {
        const scrollViewportBottomY = scrollableEl.scrollTop + scrollableEl.clientHeight
        const loadMoreY = scrollableEl.scrollHeight - loadMoreThreshold
        if (scrollViewportBottomY >= loadMoreY) {
          onLoadMore()
        }
      }, scrollThrottleFrequency)

      scrollableEl.addEventListener('scroll', onScroll)

      return () => {
        scrollableEl.removeEventListener('scroll', onScroll)
        onScroll.cancel()
      }
    },
    [ disabled, loadMoreThreshold, scrollThrottleFrequency, scrollableRef, onLoadMore ]
  )
}

module.exports = useInfiniteScroll
